import React, { useCallback, useState } from "react";
import { useField, useFormikContext } from "formik";
import { BaseInput, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { TaxIdInputProps } from "./types";

/**
 * `TaxIdInput` is a React component for inputting tax ID numbers.
 */
export const TaxIdInput: React.FC<TaxIdInputProps> = (props: TaxIdInputProps) => {
  const [field, meta, helpers] = useField<string>({
    name: "identificationNumber",
    /**
     * We must perform additional validation here outside of yup schema so we can easily get initial values
     */
    validate: (value: string) => {
      if (value !== meta.initialValue && value.length < 4) {
        return "Identification number must be at least 4 digits";
      }
      return undefined;
    },
  });
  const [isEditing, setIsEditing] = useState<boolean>(false);

  const handleEditClick = useCallback(() => {
    setIsEditing(true);
    void helpers.setValue("", false);
  }, [setIsEditing, helpers]);

  const handleResetToInitialClick = useCallback(() => {
    if (meta.initialValue) {
      void helpers.setValue(meta.initialValue);
    }
    setIsEditing(false);
  }, [meta.initialValue, setIsEditing, helpers]);

  const { isSubmitting } = useFormikContext();
  const allowEditing = (isEditing || !meta.initialValue) && !props.readOnly;

  return (
    <>
      <BaseInput
        {...field}
        errorMessage={meta.touched ? meta.error : undefined}
        onSetValue={(value) => void helpers.setValue(value)}
        key="identificationNumber"
        data-testid="identificationNumber"
        label={props.label}
        data-cy={`${field.name}-text-input`}
        readOnly={!allowEditing || props.readOnly}
        value={allowEditing ? field.value : `****${field.value.slice(-4)}`}
        disabled={isSubmitting || props?.disabled}
        isFullWidth
        endAdornment={
          !props.readOnly &&
          meta.initialValue &&
          (isEditing ? (
            <Icon
              data-testid="clear-tax-id-icon"
              type="times"
              onClick={handleResetToInitialClick}
            />
          ) : (
            <Icon data-testid="show-edit-tax-id-icon" type="edit" onClick={handleEditClick} />
          ))
        }
      />
      {props.description && <Text variant="secondary-sm">{props.description}</Text>}
    </>
  );
};
